import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSearchSuggestions } from '../../services/discoveryService';
import { addRecentSearch, clearRecentSearches, getRecentSearches } from '../../utils/recentlyViewed';
import formatPrice from '../../utils/formatPrice';
import { mediaUrl } from '../../utils/mediaUrl';

// Search with autocomplete:
//  - recent searches when the box is empty
//  - matching categories + products while typing (typo tolerant on the server)
//  - full keyboard support (↑ ↓ Enter Esc) and screen-reader combobox roles
const SearchBox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ products: [], categories: [], correctedQuery: null });
  const [recent, setRecent] = useState(getRecentSearches());
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the box in sync with the search in the URL
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('search') || '';
    if (location.pathname.startsWith('/all-product')) setQuery(q);
    setOpen(false);
  }, [location.pathname, location.search]);

  // Debounced suggestions
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setData({ products: [], categories: [], correctedQuery: null });
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      getSearchSuggestions(q, { signal: controller.signal })
        .then((d) => setData(d || { products: [], categories: [] }))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  // Close when clicking outside
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const typing = query.trim().length >= 2;

  // One flat list so arrow keys move through everything in order
  const items = useMemo(() => {
    if (!typing) return recent.map((term) => ({ type: 'recent', term }));
    return [
      { type: 'query', term: query.trim() },
      ...(data.categories || []).map((c) => ({ type: 'category', ...c })),
      ...(data.products || []).map((p) => ({ type: 'product', ...p }))
    ];
  }, [typing, recent, data, query]);

  useEffect(() => setActive(-1), [items.length, typing]);

  const goSearch = (term) => {
    const q = String(term || '').trim();
    if (!q) return;
    addRecentSearch(q);
    setRecent(getRecentSearches());
    setOpen(false);
    inputRef.current?.blur();
    navigate(`/all-product?search=${encodeURIComponent(q)}`);
  };

  const choose = (item) => {
    if (!item) return goSearch(query);
    if (item.type === 'recent' || item.type === 'query') return goSearch(item.term);
    if (query.trim()) addRecentSearch(query.trim());
    setRecent(getRecentSearches());
    setOpen(false);
    if (item.type === 'category') return navigate(item.link);
    return navigate(item.slug ? `/product/${item.slug}` : `/product/${item._id}?textile=true`);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % Math.max(items.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (open && active >= 0) choose(items[active]);
    else goSearch(query);
  };

  const highlight = (text) => {
    const q = query.trim();
    if (!q) return text;
    const i = String(text).toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark>{text.slice(i, i + q.length)}</mark>
        {text.slice(i + q.length)}
      </>
    );
  };

  const showPanel = open && (typing || recent.length > 0);
  const optionId = (i) => `search-opt-${i}`;
  let idx = -1;

  return (
    <div className="search-container m-search" ref={wrapRef}>
      <form onSubmit={onSubmit} className="search-box" role="search">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search sarees, kurtis, churidar sets…"
          aria-label="Search products"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="search-suggestions"
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          enterKeyHint="search"
        />
        {query && (
          <button
            type="button"
            className="m-search-clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
        <button type="submit" className="search-btn" aria-label="Search">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
        </button>
      </form>

      {showPanel && (
        <div className="m-search-panel" id="search-suggestions" role="listbox">
          {!typing && (
            <>
              <div className="m-search-group-head">
                <span>Recent searches</span>
                <button
                  type="button"
                  onClick={() => {
                    clearRecentSearches();
                    setRecent([]);
                  }}
                >
                  Clear
                </button>
              </div>
              {recent.map((term) => {
                idx += 1;
                const i = idx;
                return (
                  <button
                    type="button"
                    key={term}
                    id={optionId(i)}
                    role="option"
                    aria-selected={active === i}
                    className={`m-search-row ${active === i ? 'is-active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose({ type: 'recent', term })}
                  >
                    <span className="m-search-ico" aria-hidden="true">↺</span>
                    {term}
                  </button>
                );
              })}
            </>
          )}

          {typing && (
            <>
              {(() => {
                idx += 1;
                const i = idx;
                return (
                  <button
                    type="button"
                    id={optionId(i)}
                    role="option"
                    aria-selected={active === i}
                    className={`m-search-row m-search-query ${active === i ? 'is-active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose({ type: 'query', term: query })}
                  >
                    <span className="m-search-ico" aria-hidden="true">⌕</span>
                    Search for “{query.trim()}”
                  </button>
                );
              })()}

              {data.correctedQuery && (
                <p className="m-search-note">
                  Showing matches for <strong>{data.correctedQuery}</strong>
                </p>
              )}

              {data.categories?.length > 0 && (
                <>
                  <div className="m-search-group-head"><span>Categories</span></div>
                  {data.categories.map((c) => {
                    idx += 1;
                    const i = idx;
                    return (
                      <button
                        type="button"
                        key={c.link}
                        id={optionId(i)}
                        role="option"
                        aria-selected={active === i}
                        className={`m-search-row ${active === i ? 'is-active' : ''}`}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => choose({ type: 'category', ...c })}
                      >
                        <span className="m-search-ico" aria-hidden="true">▦</span>
                        <span>{highlight(c.label)}</span>
                        {c.dept && <span className="m-search-dept">in {c.dept === 'KIDS' ? 'Kids' : 'Women'}</span>}
                      </button>
                    );
                  })}
                </>
              )}

              {data.products?.length > 0 && (
                <>
                  <div className="m-search-group-head"><span>Products</span></div>
                  {data.products.map((p) => {
                    idx += 1;
                    const i = idx;
                    return (
                      <button
                        type="button"
                        key={p._id}
                        id={optionId(i)}
                        role="option"
                        aria-selected={active === i}
                        className={`m-search-row m-search-product ${active === i ? 'is-active' : ''}`}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => choose({ type: 'product', ...p })}
                      >
                        <span className="m-search-thumb">{p.image && <img src={mediaUrl(p.image)} alt="" loading="lazy" />}</span>
                        <span className="m-search-pname">
                          <span>{highlight(p.name)}</span>
                          <small>{p.subCategory}</small>
                        </span>
                        {p.price != null && <span className="m-search-price">{formatPrice(p.price)}</span>}
                      </button>
                    );
                  })}
                </>
              )}

              {!loading && !data.products?.length && !data.categories?.length && (
                <p className="m-search-note">No quick matches — press Enter to search everything.</p>
              )}
              {loading && <p className="m-search-note" aria-live="polite">Searching…</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
